import React, { useState, useEffect } from 'react';
import { fmt, api } from '../utils/api';

const PERIODS = [
  { id: 'lastWeek', label: 'Last Week' },
  { id: 'thisMonth', label: 'This Month' },
  { id: 'lastMonth', label: 'Last Month' },
];

export default function BudgetGlance({ budget }) {
  const [snapshot, setSnapshot] = useState(null);
  const [period, setPeriod] = useState('lastWeek');
  const [view, setView] = useState('merchants'); // 'merchants' | 'categories'

  useEffect(() => {
    api.getTopSnapshot().then(setSnapshot).catch(() => setSnapshot(null));
  }, []);

  if (!budget?.lastWeek || !budget?.lastMonth) return null;

  const topList = snapshot?.[period]?.[view] || [];

  return (
    <div style={styles.panel}>
      <h2 style={styles.title}>👀 At a Glance</h2>

      {budget.trailingWeeks && budget.trailingWeeks.length > 0 ? (
        budget.trailingWeeks.map((week, i) => (
          <WeekRow key={week.startDate} week={week} isCurrent={i === 0} />
        ))
      ) : (
        <GlanceRow label="Last Week" period={budget.lastWeek} />
      )}
      <GlanceRow label="Last Month" period={budget.lastMonth} />

      <div style={styles.divider} />

      <div style={styles.tabRow}>
        {PERIODS.map((p) => (
          <button
            key={p.id}
            style={{ ...styles.tab, ...(period === p.id ? styles.tabActive : {}) }}
            onClick={() => setPeriod(p.id)}
          >
            {p.label}
          </button>
        ))}
      </div>
      <div style={styles.viewToggle}>
        <button
          style={{ ...styles.viewBtn, ...(view === 'merchants' ? styles.viewBtnActive : {}) }}
          onClick={() => setView('merchants')}
        >
          Top Transactions
        </button>
        <button
          style={{ ...styles.viewBtn, ...(view === 'categories' ? styles.viewBtnActive : {}) }}
          onClick={() => setView('categories')}
        >
          Top Categories
        </button>
      </div>

      {!snapshot ? (
        <p style={styles.empty}>Loading...</p>
      ) : topList.length === 0 ? (
        <p style={styles.empty}>No spending in this period yet.</p>
      ) : (
        <div style={styles.topList}>
          {topList.map((item, i) => (
            <div key={item.label + i} style={styles.topRow}>
              <span style={styles.topLabel}>{i + 1}. {item.label}</span>
              <span style={styles.topAmt}>{fmt.currency(item.total)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function WeekRow({ week, isCurrent }) {
  const diff = week.spent - week.budget;
  const over = diff > 0.01;
  const color = over ? '#ef4444' : '#00d4aa';
  const start = new Date(`${week.startDate}T00:00:00`);
  const end = new Date(`${week.endDate}T00:00:00`);
  end.setDate(end.getDate() - 1); // endDate is exclusive - show the actual last day of the week
  const label = isCurrent
    ? 'Last Week'
    : `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}–${end.toLocaleDateString('en-US', { day: 'numeric' })}`;

  return (
    <div style={{ ...styles.row, borderColor: color }}>
      <span style={styles.label}>{label}</span>
      <span style={{ ...styles.amount, color }}>
        {fmt.currency(Math.abs(diff))} {over ? 'over' : 'under'}
      </span>
    </div>
  );
}

function GlanceRow({ label, period }) {
  const diff = period.spent - period.budget;
  const over = diff > 0.01;
  const color = over ? '#ef4444' : '#00d4aa';

  return (
    <div style={{ ...styles.row, borderColor: color }}>
      <span style={styles.label}>{label}</span>
      <span style={{ ...styles.amount, color }}>
        {fmt.currency(Math.abs(diff))} {over ? 'over' : 'under'}
      </span>
    </div>
  );
}

const styles = {
  panel: {
    background: '#111827',
    border: '1px solid #1e2a3a',
    borderRadius: '16px',
    padding: '1.25rem',
  },
  title: {
    margin: '0 0 0.75rem',
    fontSize: '0.9rem',
    fontWeight: '700',
    color: '#fff',
    fontFamily: "'Syne', sans-serif",
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.6rem 0.75rem',
    background: '#0a0f1e',
    borderRadius: '8px',
    borderLeft: '3px solid',
    marginBottom: '0.5rem',
  },
  label: {
    color: '#9ca3af',
    fontSize: '0.8rem',
    fontFamily: "'Syne', sans-serif",
  },
  amount: {
    fontSize: '0.85rem',
    fontWeight: '700',
    fontFamily: "'DM Mono', monospace",
  },
  divider: {
    height: '1px',
    background: '#1e2a3a',
    margin: '0.85rem 0',
  },
  tabRow: {
    display: 'flex',
    gap: '0.3rem',
    marginBottom: '0.5rem',
  },
  tab: {
    flex: 1,
    background: 'transparent',
    border: '1px solid #1e2a3a',
    color: '#6b7280',
    borderRadius: '6px',
    padding: '4px 6px',
    fontSize: '0.68rem',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
    whiteSpace: 'nowrap',
  },
  tabActive: {
    background: 'rgba(0, 212, 170, 0.12)',
    borderColor: '#00d4aa',
    color: '#00d4aa',
  },
  viewToggle: {
    display: 'flex',
    gap: '0.3rem',
    marginBottom: '0.6rem',
  },
  viewBtn: {
    flex: 1,
    background: 'transparent',
    border: 'none',
    borderBottom: '2px solid #1e2a3a',
    color: '#6b7280',
    padding: '4px 0',
    fontSize: '0.68rem',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
  },
  viewBtnActive: {
    borderBottom: '2px solid #00d4aa',
    color: '#00d4aa',
  },
  topList: { display: 'flex', flexDirection: 'column', gap: '2px' },
  topRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '4px 2px',
  },
  topLabel: {
    color: '#9ca3af',
    fontSize: '0.75rem',
    fontFamily: "'Syne', sans-serif",
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '65%',
  },
  topAmt: {
    color: '#e5e7eb',
    fontSize: '0.75rem',
    fontWeight: '600',
    fontFamily: "'DM Mono', monospace",
  },
  empty: {
    color: '#4b5563',
    fontSize: '0.75rem',
    textAlign: 'center',
    padding: '0.5rem 0',
  },
};
